var _ = require('underscore');
var TorsoBehavior = require('backbone-torso/modules/Behavior');
var WizardWidget = require('./WizardWidget');
var launcherAppInterface = require('./../launcherApplicationInterface');

/**
 * Perspective for sharing functionality (mainly navigation) between wizard perspectives.
 *
 * See WizardWidget for details.
 *
 * Requires that the view using this behavior has a template with a wizard-nav and wizard-buttons injection points.
 * wizardPerspectiveTemplate is a good starting point for the template and can be used entirely if the content is a single well-defined view.
 *
 * @class WizardPerspectiveBehavior
 */
var WizardPerspectiveBehavior = TorsoBehavior.extend({
  /**
   * Note: this property exists on both the class object (WizardPerspectiveBehavior.NAVIGATION_DIRECTIONS) and instance (new WizardPerspectiveBehavior(...).NAVIGATION_DIRECTIONS).
   * @property NAVIGATION_DIRECTIONS {Object} convenience reference of WizardWidget.NAVIGATION_DIRECTIONS.
   */
  NAVIGATION_DIRECTIONS: WizardWidget.NAVIGATION_DIRECTIONS,

  /**
   * Note: this property exists on both the class object (WizardPerspectiveBehavior.PAGE_NAMES) and instance (new WizardPerspectiveBehavior(...).PAGE_NAMES).
   * @property PAGE_NAMES {Object} convenience reference of WizardWidget.PAGE_NAMES.
   */
  PAGE_NAMES: WizardWidget.PAGE_NAMES,

  /**
   * @property isActive {Boolean} allows the view to progromatically turn of (false) the rendering of the wizard views.
   */
  isActive: true,

  /**
   * @method initialize
   * @param attributes {Object}
   * @param behaviorOptions {Object}
   *   @param behaviorOptions.pageName {String} the name of the page of the view using this behavior instance.
   *   @param [behaviorOptions.childPageName] {String} the name of the page of the view using this behavior instance.
   *   @param [behaviorOptions.continueAllowed] {Boolean} whether the user can continue on this page.
   *   @param [behaviorOptions.patientVecnaId] {String} the vecna id of the patient.
   *   @param [behaviorOptions.appointmentExternalId] {String} the external id of the appointment.
   *   @param [behaviorOptions.regDate] {String} the reg date of the appointments.
   *   @param [behaviorOptions.initialLoadingMessage] {String} the loading message to use while the content view loads.
   *   @param [behaviorOptions.nextAction] {Function} an optional function that returns a promise.  The function is run when the wizard widget
   *                                                  advances to the next step and any wizard specific actions (including actual navigation)
   *                                                  will only run if the action resolves.  If it is rejected the remaining wizard actions are skipped.
   *   @param [behaviorOptions.previousAction] {Function} an optional function that returns a promise.  The function is run when the wizard widget
   *                                                      advances to the previous step and any wizard specific actions (including actual navigation)
   *                                                      will only run if the action resolves.  If it is rejected the remaining wizard actions are skipped.
   *   @param [behaviorOptions.printAction] {Function} an optional function that is run when the wizard widget's print button is clicked.  There is no
   *                                                      advances to the previous step and any wizard specific actions (including actual navigation)
   *                                                      will only run if the action resolves.  If it is rejected the remaining wizard actions are skipped.
   * @param viewOptions {Object}
   *   @param [viewOptions.pageName] {String} the name of the page of the view using this behavior instance.
   *   @param [viewOptions.patientVecnaId] {String} the vecna id of the patient.
   *                                       (will only be used if behaviorOptions.patientVecnaId is not set).
   *   @param [viewOptions.appointmentExternalId] {String} the external id of the appointment
   *                                              (will only be used if behaviorOptions.appointmentExternalId is not set).
   *   @param [viewOptions.regDate] {String} the reg date of the appointments
   *                                         (will only be used if behaviorOptions.regDate is not set).
   *   @param [viewOptions.questionnaireName] {String} the name of the questionnaire being displayed.
   *   @param [viewOptions.formName] {String} the name of the form being displayed.
   *   @param [viewOptions.stepAppointmentExternalId] {String} the external id of the appointment to use for this sub-step.
   * @override
   */
  initialize: function(attributes, behaviorOptions, viewOptions) {
    this._patientVecnaId = behaviorOptions.patientVecnaId || viewOptions.patientVecnaId || this.view._patientVecnaId;
    this._appointmentExternalId = behaviorOptions.appointmentExternalId || viewOptions.appointmentExternalId || this.view._appointmentExternalId;
    this._regDate = behaviorOptions.regDate || viewOptions.regDate || this.view._regDate;

    this._pageName = viewOptions.pageName || behaviorOptions.pageName;
    this._childPageName = behaviorOptions.childPageName;
    this._continueAllowed = behaviorOptions.continueAllowed;
    this._initialLoadingMessage = behaviorOptions.initialLoadingMessage || '';

    this._questionnaireName = viewOptions.questionnaireName;
    this._formName = viewOptions.formName;
    this._stepAppointmentExternalId = viewOptions.stepAppointmentExternalId;
  },

  /**
   * Retrieves the widget for this behavior instance.  Widgets are cached so they can be shared between views.
   * @method getWizardWidget
   */
  getWizardWidget: function() {
    return WizardWidget.getWizardWidget(this._getWizardId());
  },

  /**
   * Generate the identifier for the current wizard (patient id, appt id, reg date).
   * @method _getWizardId
   * @return {{patientVecnaId: *, appointmentExternalId: *, regDate: *}}
   * @private
   */
  _getWizardId: function() {
    if (_.isFunction(this.view.getWizardId)) {
      return this.view.getWizardId();
    }
    return {
      patientVecnaId: this._patientVecnaId,
      appointmentExternalId: this._appointmentExternalId,
      regDate: this._regDate
    };
  },

  /**
   * @method setContentView
   * @param contentView {Torso.View} the view to render in the content section of the wizard template.
   */
  setContentView: function(contentView) {
    this._contentView = contentView;
  },

  mixin: {
    /**
     * Updates the wizard widget so it is focused on the page associated with the view.
     * @param wizardPageContext {Object}
     *   @param wizardPageContext.lastNavigationDirection {String} Back or Forward depending on whether the navigation to
     *                                                             this page was due to a back or forward action.
     */
    navigateToWizardPage: function(wizardPageContext) {
      this.getWizardWidget().navigateToWizardPage({
        currentPageName: this._pageName,
        currentChildPageName: this._childPageName,
        questionnaireName: this._questionnaireName,
        formName: this._formName,
        continueAllowed: this._continueAllowed,
        lastNavigationDirection: wizardPageContext.lastNavigationDirection,
        appointmentExternalId: wizardPageContext.appointmentExternalId,
        initialLoadingMessage: this._initialLoadingMessage
      });
      this.getWizardWidget().clearLoadingMessage();
    }
  },

  /**
   * The prepare of the behavior is empty so we can override w/o calling the super.
   * @method prepare
   * @override
   */
  prepare: function() {
    return {
      isInPreReg: this.view._isInPreReg || this.view.isInPreReg,
      isMultiAppointment: this.view._regDate,
      isAtHospital: launcherAppInterface.isAtHospital()
    };
  },

  /**
   * @method attachTrackedViews
   * @override
   */
  attachTrackedViews: function() {
    if (this.isActive) {
      var wizardWidget = this.getWizardWidget();
      this.view.attachView('wizard-nav', wizardWidget.wizardNavView);
      this.view.attachView('wizard-buttons', wizardWidget.wizardButtonsView);
    }
    if (this._contentView) {
      this.view.attachView('content', this._contentView);
    }
  }
});

WizardPerspectiveBehavior.NAVIGATION_DIRECTIONS = WizardWidget.NAVIGATION_DIRECTIONS;
WizardPerspectiveBehavior.PAGE_NAMES = WizardWidget.PAGE_NAMES;

module.exports = WizardPerspectiveBehavior;
